import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { ApiResponse, CustomField } from '../types'
import { apiService } from './api.service'

class CustomFieldService {
  private _listAllCache: Promise<ApiResponse<CustomField>> | null = null

  async getCustomFields(params?: { page?: number; page_size?: number }): Promise<ApiResponse<CustomField>> {
    const response = await apiService.get<ApiResponse<CustomField>>('custom_fields/', { params })
    return response.data
  }

  async listAll(): Promise<ApiResponse<CustomField>> {
    if (!this._listAllCache) {
      this._listAllCache = apiService
        .get<ApiResponse<CustomField>>('custom_fields/', {
          params: { page: 1, page_size: 100000 },
        })
        .then((response) => response.data)
    }
    return this._listAllCache
  }

  clearCache(): void {
    this._listAllCache = null
  }

  async getCustomField(id: number): Promise<CustomField> {
    const response = await apiService.get<CustomField>(`custom_fields/${id}/`)
    return response.data
  }

  async createCustomField(data: Partial<CustomField>): Promise<CustomField> {
    this.clearCache()
    const response = await apiService.post<CustomField>('custom_fields/', data)
    return response.data
  }

  async updateCustomField(id: number, data: Partial<CustomField>): Promise<CustomField> {
    this.clearCache()
    const response = await apiService.patch<CustomField>(`custom_fields/${id}/`, data)
    return response.data
  }

  async create(data: Partial<CustomField>): Promise<CustomField> {
    return this.createCustomField(data)
  }

  async update(id: number, data: Partial<CustomField>): Promise<CustomField> {
    return this.updateCustomField(id, data)
  }

  async deleteCustomField(id: number): Promise<void> {
    this.clearCache()
    await apiService.delete(`custom_fields/${id}/`)
  }

  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    includeDocumentCount?: boolean,
    extraParams?: any
  ): Promise<ApiResponse<CustomField>> {
    const params: any = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true, // Include full permission fields
    }

    if (sortField) {
      const prefix = sortReverse ? '-' : ''
      params.ordering = `${prefix}${sortField}`
    }

    if (nameFilter) {
      params.name__icontains = nameFilter
    }

    if (includeDocumentCount) {
      params.include_document_count = true
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    return this.getCustomFields(params)
  }

  async delete(customField: CustomField): Promise<void> {
    return this.deleteCustomField(customField.id)
  }

  async cloneToOrganizations(data: {
    attribute_ids: number[]
    organization_ids: number[]
  }): Promise<{
    created: Array<{ source_id: number; new_id: number; organization_id: number; name: string }>
    duplicates: Array<{ attribute_id: number; attribute_name: string; organization_id: number; reason: string }>
  }> {
    this.clearCache()
    const response = await apiService.post('custom_fields/clone_to_organizations/', data)
    return response.data
  }

  async bulkEditObjects(objects: number[], operation: string, permissions?: any, merge?: boolean): Promise<void> {
    this.clearCache()
    const params: any = {
      objects,
      object_type: 'custom_fields',
      operation,
    }

    if (operation === 'set_permissions' && permissions) {
      params.owner = permissions.owner
      params.permissions = permissions.set_permissions
      params.merge = merge
    }

    await apiService.post('bulk_edit_objects/', params)
  }
}

export const customFieldService = new CustomFieldService()
